import React from 'react';
import clsx from 'clsx';

import Icon from './Icon.js';
import Button, { ButtonStyles } from './Button.js';
import SearchFormAddressBar from './SearchFormAddressBar.js';
import styles from './CTACard.module.scss';
import { useGoogleMapsApi } from '../api/gis.js';
import { useDeviceDetection } from '../utils/DeviceDetection.js';
import { setCookie } from '../utils/InputParamsProvider';

export default function CTACard({ onSearchSubmit, collapsed }) {
    const [inputValue, setInputValue] = React.useState('');
    const [location, setLocation] = React.useState(null);
    const google = useGoogleMapsApi();
    const isMobile = useDeviceDetection();

    const [showClearButton, setShowClearButton] = React.useState(false);

    function handleInputChange(event, nextInputValue) {
        setInputValue(nextInputValue);
        setLocation(null);
    }

    function handleGeoLocation(event) {
        if (event) event.preventDefault();

        navigator.geolocation.getCurrentPosition((position) => {
            const geocoder = new google.maps.Geocoder();

            const latlng = {
                lat: parseFloat(position.coords.latitude),
                lng: parseFloat(position.coords.longitude)
            };

            geocoder.geocode({ location: latlng }, (results, status) => {
                if (status === 'OK') {
                    const result = results[0];
                    if (result) {
                        const description = result.formatted_address;
                        setInputValue(description);
                        setLocation({
                            formattedAddress: description,
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                            place: result
                        });
                    }
                } else {
                    setInputValue('');
                    setLocation(null);
                    console.error('Geocoder failed due to: ' + status);
                }
            });
        }, showPositionError);
    }

    function showPositionError(error) {
        showError(error);
    }

    function showError(error) {
        switch (error.code) {
            case error.PERMISSION_DENIED:
                console.log('User denied the request for Geolocation.');
                break;
            case error.POSITION_UNAVAILABLE:
                console.log('Location information is unavailable.');
                break;
            case error.TIMEOUT:
                console.log('The request to get user location timed out.');
                break;
            default:
                console.log('An unknown error occurred.');
        }
    }

    function initialGeoLocation() {
        navigator.geolocation.getCurrentPosition((position) => {
            const geocoder = new google.maps.Geocoder();
            const latlng = {
                lat: parseFloat(position.coords.latitude),
                lng: parseFloat(position.coords.longitude)
            };

            geocoder.geocode({ location: latlng }, (results, status) => {
                if (status === 'OK') {
                    const result = results[0];
                    if (result) {
                        const description = result.formatted_address;
                        setInputValue(description);
                    }
                } else {
                    console.error('Geocoder failed due to: ' + status);
                }
            });
        }, showPositionError);
    }

    function handleSubmit(event) {
        event.preventDefault();
        if (!inputValue) return;

        if (location && location.formattedAddress === inputValue) {
            onSearchSubmit({
                formattedAddress: location.formattedAddress,
                lat: location.lat,
                lng: location.lng,
                place: location.place
            });
            return;
        }

        const geocoder = new google.maps.Geocoder();

        geocoder.geocode(
            {
                address: inputValue,
                componentRestrictions: {
                    country: 'it'
                }
            },
            function (results, status) {
                if (status === google.maps.GeocoderStatus.OK) {
                    const result = results[0];

                    if (result) {
                        const description = result.formatted_address;
                        const point = result['geometry']['location'];

                        onSearchSubmit({
                            formattedAddress: description,
                            lat: point.lat(),
                            lng: point.lng(),
                            place: result
                        });
                    }
                } else {
                    setInputValue('');
                    setLocation(null);
                    console.error('Geocoder failed due to: ' + status);
                }
            }
        );
    }

    function handleAutocompleteSelection(event){
        const geocoder = new google.maps.Geocoder();

        geocoder.geocode(
            {
                address: inputValue,
                componentRestrictions: {
                    country: 'it'
                }
            },
            function (results, status) {
                if (status === google.maps.GeocoderStatus.OK) {
                    const result = results[0];

                    if (result) {
                        const description = result.formatted_address;
                        const point = result['geometry']['location'];

                        setCookie('lat', point.lat(), 1);
                        setCookie('lng', point.lng(), 1);
                        setCookie('formattedAddress', description, 1);
                    }
                } else {
                    setInputValue('');
                    setLocation(null);
                    console.error('Geocoder failed due to: ' + status);
                }
            }
        );
    }

    React.useEffect(() => {
        if (google) {
            initialGeoLocation();
        }
    }, [google]);



    const className = clsx(styles.root, collapsed && styles.collapsed);
    const bootstrapCardClasses = "shadow-lg rounded-lg p-4 p-md-4";

    return (

        <form className={className} onSubmit={handleSubmit}>

            <div className={bootstrapCardClasses}>


                <p className={"text-muted typo14"}>
                    UNA CONSULENZA SU MISURA
                </p>

                <h2 className={"typo32 mt-2"}>
                    Trova l'agenzia più vicina a te
                </h2>
                <p className="text-muted mb-4">
                    Sempre al tuo fianco per rispondere alle tue domande e fornirti tutta la consulenza necessaria
                </p>

                <div className={styles.addressInputRow}>
                    <div
                        className={styles.addressInputContainer}
                        onMouseOver={() => setShowClearButton(true)}
                        onMouseOut={() => setShowClearButton(false)}
                    >
                        <SearchFormAddressBar
                            className={styles.input}
                            type="text"
                            placeholder={'Inserisci indirizzo, CAP o città'}
                            inputValue={inputValue}
                            onInputChange={handleInputChange}
                            onSubmit={handleAutocompleteSelection}
                        />

                        <Button
                            icon={<Icon icon="icon/close_grey.png" />}
                            className={clsx(styles.clearAddressStyle, showClearButton && styles.show)}
                            type="button"
                            onClick={() => setInputValue('')}
                        />
                    </div>

                    <Button
                        variant={ButtonStyles.SECONDARY}
                        icon={<Icon icon="icon/geolocator_grey.png" />}
                        className={clsx(styles.userPositionStyle, isMobile && styles.mobile)}
                        onClick={handleGeoLocation}
                        type="button"
                    />
                </div>

                <Button
                    variant={ButtonStyles.PRIMARY}
                    className={"btn btn-primary mt-3"}
                    icon={
                        isMobile ? <Icon icon="icon/cerca_white.png" /> : null
                    }
                    text={isMobile ? null : 'Contatta un agente'}
                />

            </div>
        </form>
    );
}